import type { FC, FocusEvent, ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import { useCombobox } from 'downshift';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { Progress } from 'src/app/components/Elements/Progress/index';

import type { TextInputUnwiredProps } from '../TextInput';
import { TextInputUnwired } from '../TextInput';

import type { PropsFromRedux } from './container';
import { initialState } from './initialState';
import styles from './styles.module.scss';

const PAUSE_BEFORE_CLOSE_DROPDOWN = 250;
/**
 * Helper util to infer the type of an element inside an array or
 * a promise
 *
 * @see https://www.typescriptlang.org/docs/handbook/advanced-types.html
 */
type Unpacked<T> = T extends (infer U)[]
  ? U
  : T extends (...args: any[]) => infer U
  ? U
  : T extends Promise<infer U>
  ? U
  : T;

type RenderMenuItemArgs<T> = {
  item: Unpacked<Unpacked<T>>;
};

export interface AutocompleteInputProps<T> extends TextInputUnwiredProps, PropsFromRedux {
  /**
   * A function to normalize array values to a string
   */
  itemToString: (item: Unpacked<Unpacked<T>>) => string;
  /**
   * Dropdown text to display when the input is focused,
   * and has no value
   * - Optional
   */
  searchHelperText?: string;
  /**
   * Text that is appended to the new entry option at the bottom
   * of the dropdown. If none is provided, the default is "Use this name".
   * - Optional
   */
  newEntryMessage?: string;
  /**
   * Text that is shown if a search doesn't return any matching itmes
   * in the dropdown. If none is provided, the default is "No results. Please check spelling.".
   * NOTE: This will only show if onNewEntry is not an option
   * - Optional
   */
  noMatchingItemsMessage?: string;
  /**
   * Function to render DOM elements for each list item
   * - Required
   */
  renderMenuItem: ({ item }: RenderMenuItemArgs<T>) => ReactElement;
  renderCustomNewEntry?:
    | (({ item }: RenderMenuItemArgs<T>) => ReactElement)
    | (({ item }: RenderMenuItemArgs<T>, newString: string) => ReactElement);
  /**
   * A sync or async func that returns your items. This function should only
   * return a response, and should have no other side effects.
   * - Required
   */
  fetchItemsOnChange?: ((v: string) => T) | ((v: string) => Promise<T>);
  /**
   * A function that is called when users click the add new entry option.
   * - Optional
   */
  onNewEntry?: ({ item }: RenderMenuItemArgs<T>) => any | undefined;
  /**
   * This enables Downshift to update the corresponding Formik
   * field value, on input value change.
   * - Optional
   */
  onInputValueChange?: (inputValue: string) => void;
  /**
   * This enables Downshift to update the corresponding Formik
   * field value, on item selection
   */
  onSelectedItemChange?: ({ item }: RenderMenuItemArgs<T>) => void;
  /**
   * This enables functionality in the event of the selection of an item from the dropdown.
   * - Optional
   */
  onSelectedItemChangeCallback?: ({ item }: RenderMenuItemArgs<T>) => void;
  /**
   * This enables functionality in the event of the selection of an item from the downshift function selectItem.
   * - Optional
   */
  onFunctionSelectedItemChangeCallback?: ({ item }: RenderMenuItemArgs<T>) => void;
  /**
   * An item tostring function to update input value based on functionally selected item
   * - Optional
   */
  functionSelectedItemToString?: (item: Unpacked<Unpacked<T>>) => string;
  /**
   * Boolean that determines whether to show the loading spinner while autocomplete items are fetched.
   * - Optional
   */
  showLoader?: boolean;
  initialValue?: Unpacked<Unpacked<T>>;
  /**
   * Component to be rendered below the dropdown.
   * - Optional
   */
  ChildComponent?: FC<Record<string, any>>;
  childComponentProps?: object;
  altSelectAnimation?: string;
  altSelectedItemStyle?: string;
  /**
   * React element to be rendered appended to the dropdown
   * - Optional
   */
  appendedContent?: ReactNode;
  /**
   * React element to be rendered adjacent to the input
   * - Optional
   */
  AdjacentContent?: FC<Record<string, any>>;
  /**
   * Custom wrapper class name
   * - Optional
   */
  wrapperClassName?: string;
  /**
   * Debounce timer in milliseconds for dropdown item fetch
   * - Optional, Defaults to 200
   */
  debounceWaitTime: number;
  /**
   * disables input
   * - Optional, Defaults to undefined
   */
  disabled?: boolean;

  /**
   * disables/enables custom transfer form logic for medication
   * - Optional, defaults to undefined
   */
  isTransferForm?: boolean;
  /** Custom data-* attributes such as "data-private" */
  [key: `data-${string}`]: any;
}

/**
 * Styles
 * https://www.figma.com/file/NPCFOg5SP72yIb4a09eU1g/Q120_Web_TransferFormExperiement---1?node-id=56%3A2
 */

export const AutocompleteInput = ({
  itemToString,
  searchHelperText,
  renderMenuItem,
  newEntryMessage = 'Use this name',
  noMatchingItemsMessage = 'No results. Please check spelling.',
  fetchItemsOnChange,
  onNewEntry,
  onInputValueChange,
  onSelectedItemChange,
  onFunctionSelectedItemChangeCallback,
  functionSelectedItemToString,
  showLoader,
  dispatchSegmentTrack,
  onBlur,
  initialValue,
  ChildComponent,
  renderCustomNewEntry,
  childComponentProps,
  altSelectAnimation,
  altSelectedItemStyle,
  appendedContent,
  AdjacentContent,
  wrapperClassName,
  debounceWaitTime = 200,
  disabled,
  isTransferForm,
  ...props
}: AutocompleteInputProps<Record<string, any>>) => {
  const [inputItems, setInputItems] = useState(initialState);
  const [newEntry, setNewEntry] = useState({ name: '', isNewEntry: true });
  const [isLoading, setIsLoading] = useState(false);
  const [transitionItem, setTransitionItem] = useState(null);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const [isTypedInputChange, setIsTypedInputChange] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchDropdownItems = useCallback(
    async inputValue => {
      let res;
      const isValidSearchInput = inputValue && inputValue.length >= 3;
      if (isValidSearchInput) {
        if (showLoader) setIsLoading(true);
        try {
          res = (await fetchItemsOnChange?.(inputValue)) ?? initialState;
          setInputItems(res);
          if (!onNewEntry && res.length === 0) setInputItems([{ showNoMatchingItemsMessage: true }]);
        } catch (error) {
          Sentry.captureException(error);
        } finally {
          if (showLoader) setIsLoading(false);
        }
      } else {
        setInputItems(initialState);
      }
    },
    [fetchItemsOnChange, onNewEntry, showLoader]
  );

  const renderNewEntry = ({ item }) => (
    <>
      <span>"{item.name}"</span>
      <span className={styles.newEntrySuffix}>&nbsp;{`- ${newEntryMessage}`} </span>
    </>
  );

  const allItems = onNewEntry ? [...inputItems, newEntry] : inputItems;

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    selectItem,
    openMenu,
    closeMenu,
    selectedItem,
    inputValue,
  } = useCombobox({
    items: allItems,
    itemToString,
    initialInputValue: initialValue && itemToString(initialValue),
    /**
     * @see https://codesandbox.io/s/usecombobox-variations-state-reducer-x7feb
     */
    stateReducer: (state, actionAndChanges) => {
      const { changes } = actionAndChanges;
      switch (actionAndChanges.type) {
        // On esc, close the menu, and return the previous state
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          return {
            ...changes,
            selectedItem: state.selectedItem,
          };
        // On input value change.
        case useCombobox.stateChangeTypes.InputChange:
          onInputValueChange?.(changes.inputValue);
          setIsTypedInputChange(true);
          if (onNewEntry) setNewEntry(item => ({ ...item, name: changes.inputValue }));
          return changes;
        case useCombobox.stateChangeTypes.InputBlur:
          // only call `onBlur` when Downshift says so
          // this includes telling Formik to set the field as 'touched'
          const syntheticEvent = {
            target: { name: props.name, value: state.inputValue },
          } as FocusEvent<HTMLInputElement>;
          onBlur(syntheticEvent);
          // if the new input value doesn't match the selected item's
          // normalized string value,
          // - return an empty object {} to downshift
          if (changes.inputValue !== itemToString(changes.selectedItem)) {
            return {
              ...changes,
              selectedItem: {},
            };
          } else {
            // The first time the new entry option is selected with the enter key, Downshift closes the dropdown menu by default,
            // preventing the selection animation from being visible. The following code forces the menu to be open long enough to show the animation.
            if (changes.selectedItem?.isNewEntry) {
              openMenu();
              setTimeout(function () {
                closeMenu();
              }, PAUSE_BEFORE_CLOSE_DROPDOWN);
            }
            return changes;
          }
        /* falls through */
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (changes.selectedItem) {
            setTransitionItem(changes.selectedItem);
            openMenu();
            setTimeout(function () {
              setTransitionItem(null);
              closeMenu();
            }, PAUSE_BEFORE_CLOSE_DROPDOWN);

            onSelectedItemChange?.(changes.selectedItem);
            if (changes.selectedItem.isNewEntry) {
              onNewEntry?.(changes.selectedItem);
              // Update new entry text to match the newEntry
              setNewEntry(item => ({ ...item, name: changes.selectedItem.name }));
              return {
                ...changes,
                inputValue: changes.inputValue,
              };
            } else {
              // Update new entry text to selected item display
              setNewEntry(item => ({ ...item, name: itemToString(changes.selectedItem) }));

              return changes;
            }
          }
          return changes;
        case useCombobox.stateChangeTypes.FunctionSelectItem:
          onFunctionSelectedItemChangeCallback?.(changes.selectedItem);
          return {
            ...changes,
            inputValue: functionSelectedItemToString
              ? functionSelectedItemToString(changes.selectedItem)
              : changes.inputValue,
          };
        default:
          return changes; // otherwise business as usual.
      }
    },
  });

  const preventDefault = e => e.preventDefault();

  const mergeOnFocus = e => {
    const payload = {
      pagepath: window.location.pathname,
      fieldname: e?.target?.name,
    };
    dispatchSegmentTrack(UNIFIED.fieldFocused, payload);
    openMenu();
    const target = e?.target;
    // See link for a discussion why setTimout is used here to ensure proper highlighting of text on mobile devices and across browsers
    // https://stackoverflow.com/questions/54229359/why-does-select-not-work-on-safari-with-reactjs
    setTimeout(() => target?.select?.(), 0);
    props.onFocus?.(e);
  };

  const shouldShowSearchHelperText = isOpen && inputValue?.length < 1 && searchHelperText;
  const shouldShowItems = isOpen && inputValue?.length >= 1 && !shouldShowSearchHelperText;
  const isDropdownOpen = inputItems.length > 0;

  /**
   * TODO: This isn't great to put here because AutocompleteInput is meant to be a reusable component
   * That being said... There are many other spots that tie this component to specific use cases.
   *
   * The TODO would be to remove this and apply `disabled` to the item array such that
   * `getInputProps()` - when called - spreads the correct `disabled` value.
   */
  const checkIfItemIsMed = item => 'eligibleForTransfer' in item;
  const checkIfEligibleMed = item => checkIfItemIsMed(item) && item.eligibleForTransfer === true;

  useEffect(() => {
    // Note: Keeps appendedContent consistently appended to the bottom of dropdown
    if (appendedContent) setDropdownHeight(dropdownRef.current?.clientHeight + 72);
  }, [isOpen, allItems, appendedContent]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputValue?.length > 0 && isTypedInputChange) fetchDropdownItems(inputValue);
      setIsTypedInputChange(false);
    }, debounceWaitTime);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [debounceWaitTime, fetchDropdownItems, inputValue, isTypedInputChange]);

  //TODO: GROW-2451 Refactor out all components props into new component
  return (
    <>
      <div {...getComboboxProps({ className: classNames(styles.component, wrapperClassName) })}>
        <div>
          <TextInputUnwired
            {...getInputProps({
              ...props,
              onFocus: mergeOnFocus,
              value: props.value,
            })}
            labelProps={{ ...getLabelProps() }} /* @TODO Formik Error will goes in argument for `getLabelProps` */
            hasLoader={true}
            isLoading={isLoading && isDropdownOpen}
            className={styles.input}
            disabled={disabled}
          />
          <ul
            {...getMenuProps({
              className: classNames({
                [styles.compactStyle]: props.compactStyle,
                [styles.hasMaxHeight]: allItems.length > 5,
              }),
            })}
            ref={dropdownRef}
          >
            {shouldShowSearchHelperText && (
              <li onMouseDown={preventDefault} className={styles.searchText} key="search-helper">
                {searchHelperText}
              </li>
            )}
            {shouldShowItems && isLoading && !isDropdownOpen && (
              <li className={styles.loader} key="loading-indicator">
                <Progress color={'inherit'} size={20} thickness={2} />
              </li>
            )}
            {shouldShowItems &&
              allItems.map((item, index) => {
                const selectedItemIsNewEntry = highlightedIndex === -1 && selectedItem?.isNewEntry && item.isNewEntry;

                if (item.showNoMatchingItemsMessage) {
                  return (
                    <li onMouseDown={preventDefault} className={styles.searchText} key={noMatchingItemsMessage}>
                      {noMatchingItemsMessage}
                    </li>
                  );
                  // TODO: (Najja) This if statement avoids showing the new entry option with an empty set of quotation marks.
                  // This spaghetti code should be refactored.
                } else if (!(item.isNewEntry && !item.name)) {
                  return (
                    <li
                      {...getItemProps({
                        item,
                        index,
                        disabled: isTransferForm && checkIfItemIsMed(item) && !checkIfEligibleMed(item),
                      })}
                      className={classNames({
                        [altSelectedItemStyle ? altSelectedItemStyle : styles.grayHighlight]:
                          highlightedIndex === index || selectedItemIsNewEntry,
                        [styles.firstDropdown]: index === 0 && highlightedIndex === -1 && !selectedItem,
                        [altSelectAnimation ? altSelectAnimation : styles.selectAnimation]:
                          item === transitionItem || (item.isNewEntry && transitionItem?.isNewEntry),
                        // below are two special cases for medication search on transfer meds form
                        [styles.enabledMed]: isTransferForm && checkIfItemIsMed(item) && checkIfEligibleMed(item),
                        [styles.disabledMed]: isTransferForm && checkIfItemIsMed(item) && !checkIfEligibleMed(item),
                      })}
                      key={`${item.name}${index}`}
                      aria-disabled={isTransferForm && checkIfItemIsMed(item) && !checkIfEligibleMed(item)}
                    >
                      {!item.isNewEntry && renderMenuItem({ item })}
                      {item.isNewEntry &&
                        (renderCustomNewEntry
                          ? renderCustomNewEntry({ item }, newEntryMessage)
                          : renderNewEntry({ item }))}
                    </li>
                  );
                }
              })}
          </ul>

          {appendedContent && (
            <div
              style={{
                top: dropdownHeight,
                position: 'absolute',
                right: 0,
                display: isOpen ? 'block' : 'none',
                zIndex: 2,
              }}
            >
              {appendedContent}
            </div>
          )}
        </div>

        {AdjacentContent && <AdjacentContent fetchItems={() => fetchDropdownItems(inputValue)} openMenu={openMenu} />}
      </div>

      {ChildComponent && (
        <ChildComponent
          selectedItem={selectedItem}
          getItemProps={getItemProps}
          selectItem={selectItem}
          itemToString={itemToString}
          childComponentProps={childComponentProps}
        />
      )}
    </>
  );
};
